import { FC, useState } from 'react';
import { createPortal } from 'react-dom';

import { AnimationProps, motion } from 'framer-motion';
import styled from 'styled-components';

import { Button } from '@components/button';
import { ButtonGroup } from '@components/button-group';
import { FormV2 } from '@components/form/v2';
import { Header } from '@components/header';
import { IconButton } from '@components/icon-button';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { PreviewFields } from './preview-fields';
import { ViewportType } from './state';
import { ViewportActions } from './viewport-actions';

export const FullscreenPreview: FC<{
  name: string;
  fields: FormV2['fields'];
  onClose(): void;
}> = ({ children, name, fields, onClose }) => {
  const [viewport, setViewport] = useState<ViewportType>('desktop');
  const { t } = useScopedTranslate(locale);

  return createPortal(
    <Container {...animationConfig}>
      <Header
        title={<PreviewTitle onBackClick={onClose} title={name} />}
        actions={
          <Stack space={6} orientation="horizontal" align="flex-start" justify="center">
            <ViewportActions onViewportSelect={setViewport} selectedViewport={viewport} />
            <ButtonGroup align="right">
              {children}
              <Button variant="secondary" text={t('close')} onClick={onClose} autoFocus />
            </ButtonGroup>
          </Stack>
        }
      />
      <PreviewContainer>
        <PreviewFields fields={fields} viewport={viewport} />
      </PreviewContainer>
    </Container>,
    document.body,
  );
};

export const PreviewTitle = ({ title, onBackClick }: { title: string; onBackClick(): void }) => {
  return (
    <Stack space={2} orientation="horizontal">
      <IconButton
        data-cy="fullscreen-preview-back"
        icon={<IconArrowLeft />}
        variant="ghost-purple"
        onClick={onBackClick}
      />
      <PreviewTitleText>{title}</PreviewTitleText>
    </Stack>
  );
};

const locale = {
  en: {
    close: 'Close',
  },
};

const PreviewContainer = styled.div({
  position: 'relative',
  flexGrow: 1,
});

const Container = styled(motion.div)({
  zIndex: 99999,
  position: 'fixed',
  backgroundColor: theme.color.backgroundPrimary,
  display: 'flex',
  flexDirection: 'column',
});

export const PreviewTitleText = styled.div({
  height: pxToRem(40),
  display: 'flex',
  alignItems: 'center',
  font: theme.font.bold18,
  color: theme.color.textPrimary,
});

const animationConfig: AnimationProps = {
  initial: {
    left: 40,
    right: 40,
    bottom: 40,
    top: 40,
    opacity: 0,
  },
  animate: {
    left: 0,
    right: 0,
    bottom: 0,
    top: 0,
    opacity: 1,
    transition: { type: 'just' },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2,
    },
  },
};
